import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { currentObjective, isError, type Objective } from '../api';
import { samePlans } from './selector';

/**
 * One reading of "which plans does this learner have", shared with the shell.
 *
 * **The selector is in the fixed header, and the header is not a screen.** It has none of a
 * screen's state and must not acquire any — but it now has to name the plan a learner is in,
 * which means somebody has to have read the plans. The obvious spelling is a fourth fetcher in
 * `Shell`, and `FirstRunProvider` beside it is the argument against: four readers of one fact,
 * each a round trip apart, is not a stale label but a control disagreeing with the screen under
 * it about which plans exist.
 *
 * So there is one holder, and two ways to fill it:
 *
 * - **Its own read**, once, when there is a session. That is what covers the screens that read
 *   no objectives of their own — configuration and the profile, which are global by the owner's
 *   decision and have no reason to ask.
 * - **`observe`**, which is the three screens that *do* read handing over what they already
 *   have. `Chat` re-reads its plans on a timer, `Objectives` re-reads after every create, close
 *   and resume, and `Progress` reads on open — so the header's list is refreshed by the screens
 *   that make it change, at **no extra request**. A provider polling for itself would be a
 *   second `GET /api/objectives/current` every few seconds for a row of chrome.
 *
 * **A failed read leaves the list absent rather than empty**, and that distinction is the whole
 * of what the selector does with it: absent renders nothing at all, empty would render "no
 * plans" and offer to add one to a learner who may have three. `roomForAnother` makes the same
 * distinction on the same facts for the same reason.
 *
 * **But absent is now two states, and it had to become two** (F-163, found by the review). This
 * file used to say a failed read needed no report because *"every screen makes its own read and
 * reports its own failures, and this one exists only to name a row of chrome."* `/new-plan` is
 * the first screen in the app for which that sentence is false: it is about no plan, so it makes
 * no read, and the only thing it needs to know — how many plans are live — comes from here. With
 * the failure swallowed and nothing retrying, a learner whose one request was dropped got a
 * spinner that never resolved, with no selector above it (the list is absent, so the control
 * draws nothing) and no plan bar (it is not drawn on that screen at all). This project's
 * signature failure, arriving as a loading message.
 *
 * So `failed` says the read was tried and lost, and `reread` is the way out — the same pair
 * `useFirstRun` exposes, and for the same reason: *"'One moment…' for ever is the worst of the
 * three states this screen can be in."* Nothing is retried automatically; a screen that needs
 * the answer offers the button, and a row of chrome still says nothing.
 */
interface PlansState {
  /** Every plan the API last reported, or `undefined` while nobody has managed to read them. */
  plans: readonly Objective[] | undefined;
  /** The plan the API defaults to — "most recently asked from". */
  defaultId: string | undefined;
  /**
   * Whether this provider's own read was tried and lost, as opposed to not having landed.
   *
   * **`plans === undefined` alone cannot tell those apart**, and the difference is a spinner
   * against an error with a button. Only this provider's read sets it: a screen's `observe` is
   * the authority when it speaks, and a screen's own failure is that screen's to report.
   */
  failed: boolean;
  /**
   * The plan the learner last had on screen this session, which is not the same thing.
   *
   * **It exists because the global screens carry no `?o=` and must not**, by the owner's own
   * decision — so a learner reading plan B who opens configuration has, in the address, no plan
   * at all. Without this the selector would fall back to the API's default, which is plan A
   * whenever A was asked from more recently, and the header would silently re-scope somebody's
   * session by their having looked at their quiet hours. It is React state and not an address
   * on purpose: what a global screen acts on is still global, and the only thing being
   * remembered is what the header says and where the bar's three links point.
   */
  current: string | undefined;
  /** A screen handing over a fresher read of its own. See above. */
  observe: (next: { plans?: readonly Objective[]; defaultId?: string }) => void;
  /** The chrome saying which plan the address named, when it named one. */
  noteCurrent: (objectiveId: string) => void;
  /**
   * Ask again, after a read that gave up.
   *
   * For the screen that has nothing else to offer — see `failed`. It is a button a learner
   * presses, never a timer: a provider that retried on its own would be the second poll this
   * file exists to avoid.
   */
  reread: () => void;
}

const PlansContext = createContext<PlansState | undefined>(undefined);

export function PlansProvider({
  signedIn,
  children,
}: {
  signedIn: boolean;
  children: React.ReactNode;
}) {
  const [plans, setPlans] = useState<readonly Objective[] | undefined>();
  const [defaultId, setDefaultId] = useState<string>();
  const [current, setCurrent] = useState<string>();
  const [failed, setFailed] = useState(false);
  /* Bumped by `reread`, and named in the effect's dependencies — which is the whole of the
     retry. A counter rather than a flag so a second press after a second failure still fires. */
  const [round, setRound] = useState(0);

  /**
   * Whether a screen has reported since this provider's own read was sent.
   *
   * **The provider's read is the slowest thing that can answer and the least entitled to win.**
   * It is fired once at sign-in; a screen mounts immediately after and makes the same request
   * with the same latency, so the two are genuinely in a race — and if the provider's lands
   * second it replaces a list a screen has already reported with one taken at the same moment or
   * earlier. A review caught the first draft doing exactly that, and doing it with bare
   * `setPlans`, which also meant a response without `objectives` (an API older than F-151 step 6)
   * would set the list back to `undefined` and take the selector off the screen. `live` guards
   * unmount and sign-out; this guards "somebody better has spoken".
   */
  const fedByScreen = useRef(false);

  /*
   * Each field is replaced only when the caller has one, so a screen that knows the list but not
   * the default cannot blank the default — and neither can an API deployed before F-151 step 6,
   * which answers `objective` and no `objectives` at all.
   *
   * **And an unchanged list is not an update.** `Chat` re-reads its plans on a twenty-second
   * timer and hands back a fresh array every tick even when nothing in it moved; this provider
   * wraps `<Routes>`, so taking that array would re-render the shell, the header and the screen
   * four times a minute for a row of chrome that did not change. `Chat`'s own poll path already
   * makes this comparison before it sets its state ("a new array every time is a re-render of
   * the bar for nothing"); this is the same care on the path that actually feeds the selector.
   */
  const observe = useCallback((next: { plans?: readonly Objective[]; defaultId?: string }) => {
    if (next.plans !== undefined) {
      fedByScreen.current = true;
      /* A list in hand answers the question this provider's failure left open, whoever read it.
         Cleared here rather than only in the effect so a screen's successful read takes the
         error message off a screen that is showing one. */
      setFailed(false);
      setPlans((was) => (samePlans(was, next.plans) ? was : next.plans));
    }
    if (next.defaultId !== undefined) {
      fedByScreen.current = true;
      setDefaultId(next.defaultId);
    }
  }, []);

  const reread = useCallback(() => {
    setFailed(false);
    setRound((was) => was + 1);
  }, []);

  const noteCurrent = useCallback((objectiveId: string) => {
    /* Compared before it is set, because this is called from an effect on every render of the
       header: an unconditional `setCurrent` would be a state update per render, which React
       bails out of but only after re-rendering the whole tree under the provider. */
    setCurrent((was) => (was === objectiveId ? was : objectiveId));
  }, []);

  useEffect(() => {
    /*
     * Cleared on sign-out, and that is not tidiness. The selector names a plan; a list left
     * behind by the previous account would name somebody else's plan in the header above a
     * sign-in screen. `FirstRunProvider` resets its own facts on the same event, for the same
     * reason and with the same failure behind it — a browser is not a person.
     */
    if (!signedIn) {
      setPlans(undefined);
      setDefaultId(undefined);
      setCurrent(undefined);
      setFailed(false);
      fedByScreen.current = false;
      return;
    }
    let live = true;
    fedByScreen.current = false;
    void currentObjective().then((result) => {
      if (!live) return;
      /*
       * **A failure leaves the list absent rather than empty.** The selector draws nothing for
       * absent, which is the right way to be wrong: a header saying "no plans" to somebody who
       * has one is a claim, and a header saying nothing is a row that has not arrived.
       *
       * **And it now says so, which it did not** (F-163). The chrome still shows nothing and
       * still retries nothing on its own; what changed is that `failed` distinguishes this from
       * "has not landed", so the one screen with no read of its own can offer a button instead
       * of a spinner that never resolves. See the note at the top of this file.
       */
      if (isError(result)) {
        setFailed(true);
        return;
      }
      // A screen got there first; its read is at least as fresh as this one. See `fedByScreen`.
      if (fedByScreen.current) return;
      /* Through `observe` and not through `setPlans`, so this read cannot do what `observe`
         refuses to do: blank a field it has no answer for. */
      observe({ plans: result.objectives, defaultId: result.objective?.objectiveId });
      // `observe` sets the flag; this read is not a screen and must not claim to be one.
      fedByScreen.current = false;
    });
    return () => {
      live = false;
    };
  }, [signedIn, observe, round]);

  const value = useMemo<PlansState>(
    () => ({ plans, defaultId, current, failed, observe, noteCurrent, reread }),
    [plans, defaultId, current, failed, observe, noteCurrent, reread],
  );
  return <PlansContext.Provider value={value}>{children}</PlansContext.Provider>;
}

/**
 * What `usePlans` answers with no provider above it.
 *
 * **A module constant and emphatically not an object literal in the hook**, which is what this
 * was and which is an infinite render loop rather than a cosmetic waste. `Objectives` names
 * `observe` in `load`'s dependency array and runs `load` in an effect keyed on its identity — so
 * a fresh function on every render is a new `load` on every render, an effect that fires on
 * every render, and a `setState` in it that causes the next one. `useFirstRun`'s own fallback is
 * a literal and gets away with it because nothing destructures a function out of it.
 */
const NO_PROVIDER: PlansState = {
  plans: undefined,
  defaultId: undefined,
  current: undefined,
  /* Not `true`: nothing has been tried, so nothing has failed. A component rendered without a
     provider must not offer a retry for a read nobody made. */
  failed: false,
  observe: () => undefined,
  noteCurrent: () => undefined,
  reread: () => undefined,
};

/**
 * The plans, and a way to hand over a fresher reading of them.
 *
 * Answers an empty state rather than throwing when there is no provider, like `useFirstRun`:
 * the three screens that call `observe` are mounted from more than one place, and a component
 * that throws where a context is missing is a screen gone blank for the sake of a row of chrome.
 */
export function usePlans(): PlansState {
  return useContext(PlansContext) ?? NO_PROVIDER;
}
